/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-new */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useGoogleMaps } from "react-hook-google-maps";
import { Provider } from "react-redux";
import ReactDOMServer from "react-dom/server";

// region Languages
import useTranslation from "src/translations/useTranslation";
import { FilterMessages, MapMessages, VehicleTypesMessages } from "@shared/languages/interfaces";
// endregion Languages
// region Shared
import { GetVehicleTypeIdByDescription } from "@shared/constants/vehicle-types.enum";
// endregion Shared
// region Libraries
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import TrafficIcon from "@material-ui/icons/Traffic";
import _ from "lodash";
// endregion Libraries
// region Types and Interface
import { VehicleStatesTypes } from "@shared/constants/vehicle-states-types.enum";
import { GoogleApiRequestTypes } from "@shared/constants/google-api-request-types.enum";
import { VehicleAlertSummary } from "@shared/interfaces/socket-dto.interface";
import { VehicleState } from "@shared/interfaces/vehicle-state.interface";
// endregion Types and Interface
// region Utils
import mapDefaultParameters, { MAPS_API_VERSION } from "@utils/parameters";
import utils from "@utils/useful-functions";
// endregion Utils
/* region Services */
import { getAddressByCoordinates } from "@services/googleMaps/addressByCoordinates";
import { PlaceSearchResult } from "@services/googleMaps/placeSearch";
/* endregion Services */
// region Hooks
import { usePrevious } from "@hooks/usePrevious";
import { useAuth } from "@hooks/useAuth";
import { useGoogleMapUsageLog } from "@hooks/useGoogleApiUsageLog";
import { useMapFullscreen } from "@hooks/useMapFullscreen";
import AppProvider from "../../../hooks";
// endregion Hooks
// region Components
import TimeLineItem from "../../TimeLines/TimeLineVehicles/TimeLineVehiclesItem";
// endregion Components
// region Store
import store from "../../../store";
import { VehicleStatesList } from "../../../store/ducks/Vehicles/VehiclesStates/vehicles-states.type";
// endregion Store
// region Styles
import PlaceSearchField from "../PlaceSearchField";
import { ContainerMapAllVehiclesCoords } from "./styles";
// endregion Styles

interface MarkersVisualizationTypes {
  licensePlate: boolean;
  name: boolean;
}

type MapAllVehiclesCoordsProps = {
  mapHeight: string,
  vehicles: VehicleStatesList,
  markersVisualizationType: MarkersVisualizationTypes
}
const MapAllVehiclesCoords = React.memo<MapAllVehiclesCoordsProps>((
  { mapHeight, vehicles, markersVisualizationType }
) => {

  const GoogleApiKey = localStorage.getItem("@Fleet:google");
  const mapsDefaultConfig = {
    center: mapDefaultParameters.center,
    zoom: 4
  };
  const { ref, map } = useGoogleMaps(
    `${GoogleApiKey}&libraries=places,drawing&language=pt-BR&v=${MAPS_API_VERSION}`,
    mapsDefaultConfig
  );

  const { t } = useTranslation();
  const { user } = useAuth();

  // Merge all vehicles in one array (Removing repeat and ordering by vehicle code)
  const allVehicles = useMemo<VehicleStatesList>(() => (

    _.groupBy<VehicleAlertSummary>(
      _.orderBy<VehicleAlertSummary>(
        _.uniqBy<VehicleAlertSummary>(Object.keys(vehicles).reduce((acc, cv) => [...acc, ...vehicles[cv]], [] as any[]), "id_vehicle"),
        "code"
      ),
      "type.description"
    )

  ), [vehicles]);

  const vehicleCount = useMemo(() => Object.values(allVehicles).reduce((acc, group) => acc + group.length, 0), [allVehicles]);

  // Traffic Layer States
  const [control, setControl] = useState<HTMLDivElement>();
  const [showTrafficLayer, setShowTrafficLayer] = useState<boolean>(true);
  const [trafficLayer, setTrafficLayer] = useState<google.maps.TrafficLayer | null>(null);

  // Accordion expand control
  const [expandedVehicleTypes, setExpandedVehicleTypes] = useState<string[]>([]);

  const [placeSearchClearSignal, setPlaceSearchClearSignal] = useState(0);

  useGoogleMapUsageLog(map, GoogleApiRequestTypes.ALL_VEHICLES_MAP);
  useMapFullscreen(map);

  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [markerAddress, setMarkerAddress] = useState<google.maps.Marker>();

  const [circles, setCircles] = useState<google.maps.Circle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<VehicleStatesList>({});
  const [activeBoxFilter, setActiveBoxFilter] = useState(false);
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow>({} as google.maps.InfoWindow);
  const [infoWindowContent, setInfoWindowContent] = useState<string>("");
  const [contentTextFilter, setContentTextFilter] = useState("");
  const previousActiveBoxFilter = usePrevious(activeBoxFilter);

  const setMarkersValues = useCallback((vehicle: VehicleAlertSummary) => {

    // Visualization with name and license plate
    if ((markersVisualizationType.licensePlate && markersVisualizationType.name)) {
      return {
        position: {
          lat: Number(vehicle.last_coord?.latitude),
          lng: Number(vehicle.last_coord?.longitude)
        },
        icon: {
          url: utils.getMarkerAccordingVehicleType(
            vehicle,
            vehicle?.states[vehicle?.states?.length - 1]?.status?.id_vehicle_state_type || "",
            markersVisualizationType
          ),
          scaledSize: new google.maps.Size(3 * 40, 3 * 40),
          anchor: new google.maps.Point(16.5, 47.5)
        }
      };

    // Visualization with only name
    } if (markersVisualizationType.name) {
      return {
        position: {
          lat: Number(vehicle.last_coord?.latitude),
          lng: Number(vehicle.last_coord?.longitude)
        },
        icon: {
          url: utils.getMarkerAccordingVehicleType(
            vehicle,
            (vehicle?.states[vehicle?.states?.length - 1]?.status?.id_vehicle_state_type || "") as VehicleStatesTypes,
            markersVisualizationType
          ),
          scaledSize: new google.maps.Size(88, 88),
          anchor: new google.maps.Point(16.5, 47.5)
        }
      };
    // Visualization with only license plate
    } if (markersVisualizationType.licensePlate) {
      return {
        position: {
          lat: Number(vehicle.last_coord?.latitude),
          lng: Number(vehicle.last_coord?.longitude)
        },
        icon: {
          url: utils.getMarkerAccordingVehicleType(
            vehicle,
            (vehicle?.states[vehicle?.states?.length - 1]?.status?.id_vehicle_state_type || "") as VehicleStatesTypes,
            markersVisualizationType
          ),
          scaledSize: new google.maps.Size(3 * 40, 3 * 40),
          anchor: new google.maps.Point(16.5, 47.5)
        }
      };
    }

    // Visualization with just icon
    return {
      position: {
        lat: Number(vehicle.last_coord?.latitude),
        lng: Number(vehicle.last_coord?.longitude)
      },
      icon: {
        url: utils.getMarkerAccordingVehicleType(
          vehicle,
          (vehicle?.states[vehicle?.states?.length - 1]?.status?.id_vehicle_state_type || "") as VehicleStatesTypes,
          markersVisualizationType
        ),
        scaledSize: new google.maps.Size(47.5, 47.5)
      },
      optimized: vehicleCount > 256 ? false : undefined // optimized: true => breaks marker map rendering
    };
  }, [markersVisualizationType, vehicleCount]);

  /** Draw vehicles coords as a custom markers (According vehicle type and vehicle state)
   */
  const drawVehiclesPoints = useCallback(() => {

    const bounds = new google.maps.LatLngBounds();
    const arrFilteredVehicles: string[] = Object.keys(filteredVehicles).reduce((acc, cv) => (
      [...acc, ...filteredVehicles[cv].map((icv) => icv.id_vehicle)] as any
    ), [] as string[]);

    Object.keys(allVehicles).forEach((key) => {
      allVehicles[key].forEach((vehicle) => {

        // Just vehicles with last coord
        if (!_.isEmpty(vehicle.last_coord?.latitude) && !_.isEmpty(vehicle.last_coord?.longitude)) {

          let instancedMarker: google.maps.Marker = {} as google.maps.Marker;

          // Verify if vehicle marker already instanced
          markers.forEach((marker) => {
            if (marker.get("id") === vehicle.id_vehicle) instancedMarker = marker;
          });

          // Verify if vehicle marker already exists
          // IF EXISTS => Update your values (coord, marker, infowindow)
          // IF NOT EXISTS => Create new marker
          if (!_.isEmpty(instancedMarker)) {

            // Change icon and position of marker
            instancedMarker.setValues(setMarkersValues(vehicle));

            // If already have a info window open, re-render
            if (!_.isEmpty(document.getElementById(`map-all-vehicles-${vehicle.id_vehicle}`))) {
              ReactDOM.render(
                <Provider store={store}>
                  <AppProvider>
                    <TimeLineItem vehicles={[vehicle]} showAllVehicles showTimeLine showMap={false} />
                  </AppProvider>
                </Provider>,
                document.getElementById(`map-all-vehicles-${vehicle.id_vehicle}`)
              );
            }

          } else {

            // Set marker on map
            instancedMarker = new google.maps.Marker({
              ...setMarkersValues(vehicle),
              map
            });

            instancedMarker.set("id", vehicle.id_vehicle);
            instancedMarker["infowindow"] = new google.maps.InfoWindow(); // Instance the info window component (of each vehicle)

            // Create event click in marker with his info
            google.maps.event.addListener(instancedMarker, "click", ((marker, content, infoWindow) => () => {

              instancedMarker["infowindow"].setContent(`<div id="map-all-vehicles-${vehicle.id_vehicle}"></div>`);
              instancedMarker["infowindow"].open(map, marker);

              setTimeout(() => {
                ReactDOM.render(
                  <Provider store={store}>
                    <AppProvider>
                      <TimeLineItem vehicles={[vehicle]} showAllVehicles showTimeLine showMap={false} />
                    </AppProvider>
                  </Provider>,
                  document.getElementById(`map-all-vehicles-${vehicle.id_vehicle}`)
                );
              }, 0);

            })(instancedMarker, instancedMarker["infowindow"]));

            setMarkers((state) => [...state, instancedMarker]);
          }

          // Hide markers and open infowindow if not filtered on list
          // Only add marker in bounds if marker is visible
          if (activeBoxFilter && !arrFilteredVehicles.includes(instancedMarker.get("id"))) {

            instancedMarker.setVisible(false);
            instancedMarker["infowindow"].close();

          } else {

            instancedMarker.setVisible(true);
            bounds.extend(instancedMarker.getPosition() as google.maps.LatLng);
          }
        }
      });
    });

    // Recenter bound based on filtered vehicles
    if ((previousActiveBoxFilter !== activeBoxFilter)) {
      map.fitBounds(bounds);
    }

  }, [map, markers, allVehicles, setMarkersValues, filteredVehicles, activeBoxFilter, previousActiveBoxFilter]);

  /** Find address according coords and set info in state
   * @param latLng Coords to search address
   */
  const getAddress = useCallback(async (latLng) => {

    let listenerMarker;

    setInfoWindowContent(ReactDOMServer.renderToString(<CircularProgress />));
    if (_.isEmpty(infoWindow)) setInfoWindow(new google.maps.InfoWindow());

    // The platform's own geocoder first, Google only when it has no answer
    const address = await getAddressByCoordinates(latLng, { name: user.name, email: user.email });

    if (address) {

      setInfoWindowContent(address.formatted_address);

      if (map) {
        map.setZoom(15);
        map.panTo(latLng);
      }

    } else setInfoWindowContent(t(MapMessages.errorFetchingAddressData));

    return () => {
      if (typeof window !== "undefined" && window.google) google.maps.event.removeListener(listenerMarker);
    };

  }, [infoWindow, t, user.name, user.email]);

  /** Pin a marker in map
   * @param location Location (lat, lng) to marker in map
   */
  const placeMarker = useCallback((location) => {
    setMarkerAddress(
      new google.maps.Marker({
        position: location,
        map
      })
    );
    // Get address data by coord
    getAddress(location);

  }, [markerAddress, map, getAddress]);

  /** Pin the place chosen in the address search (or the typed coordinates)
   * @param result Chosen place
   */
  const handlePlaceSelect = useCallback(({ location }: PlaceSearchResult) => placeMarker(location), [placeMarker]);

  useEffect(() => {
    let  listenerMarker;

    // Set content of infoWindow and open
    if (!_.isEmpty(infoWindow)) {
      infoWindow.setContent(infoWindowContent.replace(/,/g, "<br>"));
      infoWindow.setOptions({ maxWidth: 200 });
      infoWindow.open(map, markerAddress);
      listenerMarker = infoWindow.addListener("closeclick", () => markerAddress?.setVisible(false));
    }

    return () => {
      if (!_.isEmpty(infoWindow)) infoWindow.close();
      setPlaceSearchClearSignal((signal) => signal + 1);
      if (typeof window !== "undefined" && window.google) google.maps.event.removeListener(listenerMarker);
    };

  }, [infoWindow, infoWindowContent, map, markerAddress]);

  useEffect(() => {

    let listenerMarker;

    // Init map
    if (map) {
      navigator.geolocation.getCurrentPosition((position) => {
        if (map.getCenter().lat() === 0) map.setCenter(new google.maps.LatLng(position.coords.latitude, position.coords.longitude));
      });

    }

    return () => {
      if (typeof window !== "undefined" && window.google) google.maps.event.removeListener(listenerMarker);
    };

  }, [map, t]);

  /** Draw plants circles */
  const drawPlantsCircles = useCallback(() => {

    // Transform object vehicles into array
    const flatVehicles = Object.keys(vehicles).reduce((acc, key) => [...acc, ...vehicles[key]], [] as VehicleAlertSummary[]);

    // Get all states 'Na usina' into an array
    const flatStatesNaUsina = flatVehicles.reduce((acc, vehicle) => {

      const statesNaUsina = vehicle.states.filter((state) => state.status.description === "Na usina" && !_.isEmpty(state.location));

      return [...acc, ...statesNaUsina];
    }, [] as VehicleState[]);

    interface PlantLocations {
      latitude: string;
      longitude: string;
      radius: number;
      id: string;
    }

    // Extract all plants locations from 'Na usina' states
    const flatPlantsLocations = _.uniqBy<PlantLocations>(flatStatesNaUsina.reduce((acc, state) => {
      const location = {
        latitude: state.location.latitude,
        longitude: state.location.longitude,
        radius: state.location.radius ?? 80,
        id: `${state.location.latitude},${state.location.longitude}`
      };

      return [...acc, location];
    }, [] as PlantLocations[]), "id");

    if (flatPlantsLocations) {
      for (const plant of flatPlantsLocations) {
        let instancedCircle: google.maps.Circle = {} as google.maps.Circle;

        // Verify if plant circle already instanced
        circles.forEach((circle) => {
          if (circle.get("id") === plant.id) instancedCircle = circle;
        });

        const circleConfig = {
          strokeColor: "#00FF00",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "#00FF00",
          fillOpacity: 0.35,
          center: { lat: Number.parseFloat(plant.latitude), lng: Number.parseFloat(plant.longitude) },
          radius: plant.radius
        };

        if (!_.isEmpty(instancedCircle)) {

          instancedCircle.setValues(circleConfig);

        } else {

          // Set marker on map
          instancedCircle = new google.maps.Circle({
            ...circleConfig,
            map
          });

          instancedCircle.set("id", plant.id);

          setCircles((state) => [...state, instancedCircle]);
        }
      }
    }

  }, [map, vehicles, circles]);

  /** Filter vehicles according input */
  const handleFilter = useCallback((inputContent) => {

    const vehiclesToFilter = { ...allVehicles };

    Object.keys(vehiclesToFilter).forEach((key) => {
      vehiclesToFilter[key] = vehiclesToFilter[key].filter((vehicle) => (
        [vehicle.code, vehicle.license_plate, vehicle.description].some((el) => `${el}`.toLowerCase().search(inputContent.toLowerCase()) >= 0)
      ));
    });

    setActiveBoxFilter(true);
    setFilteredVehicles(vehiclesToFilter);

  }, [allVehicles]);

  const zoomToVehicle = (vehicle: VehicleAlertSummary) => {

    const marker = markers.find((m) => m.get("id") === vehicle.id_vehicle);

    if (marker) {
      map.panTo(marker.getPosition());
      map.setZoom(15);
    } else {
      map.panTo(mapsDefaultConfig.center);
      map.setZoom(4);
    }
  };

  // Draw and redraw vehicle markers
  useEffect(() => {
    if (map) {
      // First create circles, then markers to apply fit bounds
      drawPlantsCircles();
      drawVehiclesPoints();

    }
  },
  // eslint-disable-next-line
  [vehicles, markersVisualizationType, drawVehiclesPoints, drawPlantsCircles]);

  useEffect(() => {

    if (map) {
      if (markers.length > 0 && (map.getCenter().lat() === mapsDefaultConfig.center.lat && map.getCenter().lng() === mapsDefaultConfig.center.lng)) {
        // Await map initialize to fit bounds
        setTimeout(() => {
          const bounds = new google.maps.LatLngBounds();

          markers.forEach((marker) => bounds.extend(marker.getPosition() as google.maps.LatLng));

          map.fitBounds(bounds);
        }, 1);
      }
    }

  });

  // Instance traffic layer
  useEffect(() => {
    if (map && !trafficLayer) {
      const layer = new google.maps.TrafficLayer();

      layer.setMap(map);
      setTrafficLayer(layer);
    }
  }, [map, trafficLayer]);

  // Make Traffic Layer show / hide
  useEffect(() => {
    if (map && trafficLayer) {
      if (showTrafficLayer) {
        trafficLayer.setMap(map);
      } else {
        trafficLayer.setMap(null);
      }
    }
  }, [trafficLayer, map, showTrafficLayer]);

  // Draw traffic layer into google maps
  useEffect(() => {
    if (map) {

      if (!control && trafficLayer) {
        const customControl = {
          position: 5,
          element: "SEM USO",
          action: () => { setShowTrafficLayer((state) => !state); }
        };

        const controlDiv = document.createElement("div");
        const controlUI = document.createElement("div");
        const controlIcon = document.createElement("div");

        controlUI.style.background = "none rgb(255, 255, 255)";
        controlUI.style.border = "2px solid #fff";
        controlUI.style.borderRadius = "2px";
        controlUI.style.boxShadow = "rgb(0 0 0 / 30%) 0px 1px 4px -1px";
        controlUI.style.cursor = "pointer";
        controlUI.style.marginLeft = "10px";
        controlUI.style.marginTop = "30px";
        controlUI.title = t(MapMessages.updateVehicleCurrentPosition);
        controlDiv.appendChild(controlUI);

        // Set CSS for the control interior.
        // eslint-disable-next-line max-len
        controlIcon.innerHTML = ReactDOMServer.renderToString(<TrafficIcon />);
        controlIcon.style.display = "flex";
        controlIcon.style.padding = "5px";
        controlUI.appendChild(controlIcon);

        // Add action on control and put inside map
        controlDiv.addEventListener("click", () => customControl.action());
        map.controls[customControl.position].push(controlDiv);

        setControl(controlDiv);
      }
    }
  }, [map, trafficLayer, control, t]);

  useEffect(() => {

    // Just write 2 or more letters
    if (contentTextFilter.length >= 2) handleFilter(contentTextFilter);
    else setActiveBoxFilter(false);

  }, [vehicles, contentTextFilter, handleFilter]);

  return (
    <ContainerMapAllVehiclesCoords mapHeight={mapHeight}>
      <Grid container spacing={1}>
        <Grid className="map" item xs={ 12 } md={ 12 } lg={ 9 }>
          <div className="map-input-wrapper">
            <div id="pac2-container">
              <PlaceSearchField
                id="pac2-input"
                placeholder={t(MapMessages.addressSearchLabel)}
                label={t(MapMessages.addressSearchLabel)}
                fullWidth
                map={map}
                onSelect={handlePlaceSelect}
                clearSignal={placeSearchClearSignal}
              />
            </div>
            <div ref={ ref } style={ { width: "100%", height: mapHeight, marginTop: "8px" } } />
          </div>
        </Grid>
        <Grid className="filter" item xs={ 12 } md={ 12 } lg={ 3 }>
          <TextField
            className="input"
            value={contentTextFilter}
            label={t(MapMessages.allVehiclesSearchLabel)}
            variant="outlined"
            onChange={(e) => setContentTextFilter(e.target.value)}
          />
          <div className="vehicles">
            {
              Object.keys(activeBoxFilter ? filteredVehicles : allVehicles).map((key) => {
                const label = t(VehicleTypesMessages[GetVehicleTypeIdByDescription(key)]);

                if ((activeBoxFilter ? filteredVehicles : allVehicles)[key].length < 1) {
                  return <> </>;
                }

                return (
                  <Accordion
                    onChange={() => setExpandedVehicleTypes((state) => {
                      if (state.includes(key)) {
                        return state.filter((item) => item !== key);
                      }

                      return [...state, key];
                    })}
                    expanded={expandedVehicleTypes.includes(key)}
                    key={`vehicle-list-filter-${key}`}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <Typography component="div">{label}</Typography>
                    </AccordionSummary>
                    {expandedVehicleTypes.includes(key) && (
                      <AccordionDetails>
                        <Typography component="div">
                          <TimeLineItem
                            vehicles={(activeBoxFilter ? filteredVehicles : allVehicles)[key]}
                            showAllVehicles
                            showTimeLine={false}
                            position="menu"
                            onExpandedVehicle={zoomToVehicle}
                          />
                        </Typography>
                      </AccordionDetails>
                    )}
                  </Accordion>
                );
              })
            }
          </div>
        </Grid>
      </Grid>
    </ContainerMapAllVehiclesCoords>
  );
});

export { MapAllVehiclesCoords };
export type { MarkersVisualizationTypes };
